import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    Box, Typography, Paper, Grid, Container, Button,
    Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Chip, TextField, Fade, Avatar, Divider,
    FormControl, InputLabel, Select, MenuItem, CircularProgress,
    IconButton, Tooltip, LinearProgress, InputAdornment,
    Tabs, Tab, Card, CardContent, CardActions,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Assignment as AssignmentIcon,
    Class as ClassIcon,
    Subject as SubjectIcon,
    CalendarMonth as CalendarIcon,
    Search as SearchIcon,
    History as HistoryIcon,
    ArrowBack as ArrowBackIcon,
    EventNote as TaskIcon,
    Timer as TimerIcon,
    CheckCircle as DoneIcon,
    Error as OverdueIcon,
    NotificationsActive as ActiveIcon,
    TrendingUp as StatsIcon,
    People as PeopleIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import axios from 'axios';
import styled, { keyframes } from 'styled-components';
import { useToast } from '../../context/ToastContext';
import { CustomNepaliDatePicker, getCurrentBSDate, formatDatePart, getMonthNameFromDate } from '../../utils/customDate';

// Animations
const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

// Styled Components
const PageWrapper = styled(Box)`
    background-color: #f8fafc;
    min-height: 100vh;
    padding-bottom: 80px;
    animation: ${fadeIn} 0.5s ease-out;
`;

const PremiumHeader = styled(Box)`
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    padding: 60px 0 80px;
    color: white;
    position: relative;
    overflow: hidden;
    
    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 600px;
        height: 600px;
        background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
        border-radius: 50%;
        transform: translate(30%, -30%);
    }
`;

const ContentContainer = styled(Container)`
    margin-top: -60px;
    position: relative;
    z-index: 2;
`;

const PremiumCard = styled(Paper)`
    border-radius: 24px !important;
    border: 1px solid rgba(255, 255, 255, 0.8) !important;
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(20px) !important;
    box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.05) !important;
    overflow: hidden;
    margin-bottom: 30px;
`;

const StatCard = styled(Paper)`
    padding: 20px;
    border-radius: 20px;
    display: flex;
    align-items: center;
    gap: 16px;
    background: white;
    border: 1px solid #eef2ff;
    transition: all 0.3s ease;
    
    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 24px -10px rgba(99, 102, 241, 0.15);
    }
`;

const HomeworkCard = styled(Card)`
    border-radius: 20px !important;
    border: 1px solid #e2e8f0 !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    height: 100%;
    display: flex;
    flex-direction: column;

    &:hover {
        transform: translateY(-8px);
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
        border-color: #6366f1 !important;
    }
`;

const Homework = () => {
    const { currentUser } = useSelector((state) => state.user);
    const { showToast } = useToast();

    // Tab state
    const [tabValue, setTabValue] = useState(0);

    const [homeworkList, setHomeworkList] = useState([]);
    const [classes, setClasses] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedClassFilter, setSelectedClassFilter] = useState('all');

    // UI states
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [homeworkToDelete, setHomeworkToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Form state
    const [newHomework, setNewHomework] = useState({
        title: '',
        description: '',
        dueDate: getCurrentBSDate(),
        sclassID: '',
        subjectID: ''
    });
    const [editMode, setEditMode] = useState(false);
    const [editingHwId, setEditingHwId] = useState(null);

    const teacherId = currentUser?._id;
    const schoolId = currentUser?.school?._id || currentUser?.school;

    useEffect(() => {
        if (teacherId) {
            fetchTeacherClasses();
            fetchHomework();
        }
    }, [teacherId]);

    useEffect(() => {
        if (newHomework.sclassID) {
            fetchSubjects(newHomework.sclassID);
        }
    }, [newHomework.sclassID]);

    const fetchTeacherClasses = async () => {
        try {
            const result = await axios.get(`${process.env.REACT_APP_BASE_URL}/Teacher/Classes/${teacherId}`);
            if (Array.isArray(result.data)) {
                setClasses(result.data);
                if (result.data.length > 0 && !newHomework.sclassID && !editMode) {
                    setNewHomework(prev => ({ ...prev, sclassID: result.data[0]._id }));
                }
            }
        } catch (error) {
            console.error('Error fetching classes:', error);
        }
    };

    const fetchSubjects = async (classId) => {
        try {
            const result = await axios.get(`${process.env.REACT_APP_BASE_URL}/Teacher/Subjects/${teacherId}/${classId}`);
            if (Array.isArray(result.data)) {
                setSubjects(result.data);
            } else {
                setSubjects([]);
            }
        } catch (error) {
            console.error('Error fetching subjects:', error);
            setSubjects([]);
        }
    };

    const fetchHomework = async () => {
        setLoading(true);
        try {
            const result = await axios.get(`${process.env.REACT_APP_BASE_URL}/Homework/${teacherId}`);
            if (Array.isArray(result.data)) {
                setHomeworkList(result.data);
            } else {
                setHomeworkList([]);
            }
        } catch (error) {
            console.error('Error fetching homework:', error);
            showToast('Error loading homework', 'error');
        }
        setLoading(false);
    };

    const handleCreateHomework = async () => {
        if (!newHomework.title || !newHomework.description || !newHomework.dueDate || !newHomework.sclassID) {
            showToast('Please complete all required fields', 'warning');
            return;
        }

        setSaving(true);
        try {
            if (editMode) {
                await axios.put(`${process.env.REACT_APP_BASE_URL}/Homework/${editingHwId}`, {
                    ...newHomework,
                    teacherID: teacherId,
                    schoolID: schoolId
                });
                showToast('Homework updated successfully!', 'success');
            } else {
                await axios.post(`${process.env.REACT_APP_BASE_URL}/HomeworkCreate`, {
                    ...newHomework,
                    teacherID: teacherId,
                    schoolID: schoolId
                });
                showToast('Homework assigned successfully!', 'success');
            }
            
            cancelEdit();
            fetchHomework();
        } catch (error) {
            console.error('Error saving homework:', error);
            showToast(`Error ${editMode ? 'updating' : 'creating'} homework`, 'error');
        }
        setSaving(false);
    };

    const handleEditClick = (hw) => {
        setEditMode(true);
        setEditingHwId(hw._id);
        setNewHomework({
            title: hw.title,
            description: hw.description,
            dueDate: hw.dueDate.split('T')[0],
            sclassID: hw.sclass?._id || hw.sclass,
            subjectID: hw.subject?._id || hw.subject || ''
        });
        setTabValue(1); // Switch to form
        showToast('Editing assignment', 'info');
    };

    const cancelEdit = () => {
        setEditMode(false);
        setEditingHwId(null);
        setNewHomework({ 
            title: '', 
            description: '', 
            dueDate: getCurrentBSDate(), 
            sclassID: classes[0]?._id || '', 
            subjectID: '' 
        });
        setTabValue(0);
    };

    const handleDeleteClick = (hw) => {
        setHomeworkToDelete(hw);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!homeworkToDelete) return;
        setDeleting(true);
        try {
            await axios.delete(`${process.env.REACT_APP_BASE_URL}/Homework/${homeworkToDelete._id}`);
            showToast('Homework deleted successfully', 'success');
            fetchHomework();
        } catch (error) {
            console.error('Error deleting homework:', error);
            showToast('Error deleting homework', 'error');
        }
        setDeleting(false);
        setDeleteDialogOpen(false);
        setHomeworkToDelete(null);
    };

    const isOverdue = (dueDate) => {
        const today = getCurrentBSDate();
        return formatDatePart(dueDate) < today;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const cleanDate = formatDatePart(dateString);
        return cleanDate;
    };

    const filteredHomework = useMemo(() => {
        let list = homeworkList;
        if (selectedClassFilter !== 'all') {
            list = list.filter(hw => (hw.sclass?._id || hw.sclass) === selectedClassFilter);
        }
        if (searchTerm) {
            list = list.filter(hw => 
                hw.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                hw.description.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        return list;
    }, [homeworkList, selectedClassFilter, searchTerm]);

    const stats = useMemo(() => {
        const total = homeworkList.length;
        const active = homeworkList.filter(hw => !isOverdue(hw.dueDate)).length;
        const expired = total - active;
        return { total, active, expired };
    }, [homeworkList]);

    return (
        <PageWrapper>
            <PremiumHeader>
                <Container maxWidth="lg">
                    <Fade in timeout={800}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <IconButton onClick={() => window.history.back()} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                                    <ArrowBackIcon />
                                </IconButton>
                                <Box>
                                    <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.02em' }}>
                                        Assignments
                                    </Typography>
                                    <Typography variant="h6" sx={{ opacity: 0.8, fontWeight: 400 }}>
                                        Manage student homework and track deadlines.
                                    </Typography>
                                </Box>
                            </Box>
                            
                            <Box sx={{ 
                                bgcolor: 'rgba(255,255,255,0.1)', 
                                p: 0.5, 
                                borderRadius: '16px', 
                                backdropFilter: 'blur(10px)',
                                zIndex: 10,
                                position: 'relative'
                            }}>
                                <Tabs 
                                    value={tabValue} 
                                    onChange={(e, v) => setTabValue(v)}
                                    sx={{ 
                                        minHeight: 'auto',
                                        '& .MuiTabs-indicator': { height: '100%', borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.2)', zIndex: 0 },
                                        '& .MuiTab-root': { minHeight: 'auto', py: 1.5, px: 3, color: 'white', fontWeight: 700, zIndex: 1, textTransform: 'none', borderRadius: '12px', '&.Mui-selected': { color: 'white' } }
                                    }}
                                >
                                    <Tab icon={<AssignmentIcon sx={{ mr: 1 }} />} iconPosition="start" label="All Tasks" />
                                    <Tab icon={editMode ? <EditIcon sx={{ mr: 1 }} /> : <AddIcon sx={{ mr: 1 }} />} iconPosition="start" label={editMode ? "Edit Task" : "Assign New"} />
                                </Tabs>
                            </Box>
                        </Box>
                    </Fade>
                </Container>
            </PremiumHeader>

            <ContentContainer maxWidth="lg" sx={{mt:2}} >
                {tabValue === 0 ? (
                    <Fade in timeout={500}>
                        <Box>
                            {/* Stats Dashboard */}
                            <Grid container spacing={3} sx={{ mb: 4, mt: 2 }}>
                                <Grid item xs={12} sm={4}>
                                    <StatCard elevation={0}>
                                        <Avatar sx={{ bgcolor: '#e0f2fe', color: '#0ea5e9' }}><AssignmentIcon /></Avatar>
                                        <Box>
                                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>TOTAL ASSIGNED</Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 900 }}>{stats.total}</Typography>
                                        </Box>
                                    </StatCard>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <StatCard elevation={0}>
                                        <Avatar sx={{ bgcolor: '#f0fdf4', color: '#10b981' }}><ActiveIcon /></Avatar>
                                        <Box>
                                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>ACTIVE TASKS</Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 900 }}>{stats.active}</Typography>
                                        </Box>
                                    </StatCard>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <StatCard elevation={0}>
                                        <Avatar sx={{ bgcolor: '#fef2f2', color: '#ef4444' }}><OverdueIcon /></Avatar>
                                        <Box>
                                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700 }}>EXPIRED</Typography>
                                            <Typography variant="h5" sx={{ fontWeight: 900 }}>{stats.expired}</Typography>
                                        </Box>
                                    </StatCard>
                                </Grid>
                            </Grid>

                            {/* Action Bar */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
                                <Box sx={{ display: 'flex', gap: 2, flexGrow: 1 }}>
                                    <TextField 
                                        placeholder="Search by title..." 
                                        size="small" 
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                                        sx={{ width: 350, bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                    />
                                    <FormControl size="small" sx={{ width: 200 }}>
                                        <InputLabel>All Classes</InputLabel>
                                        <Select
                                            value={selectedClassFilter}
                                            label="All Classes"
                                            onChange={(e) => setSelectedClassFilter(e.target.value)}
                                            sx={{ borderRadius: '12px', bgcolor: 'white' }}
                                        >
                                            <MenuItem value="all">All Classes</MenuItem>
                                            {classes.map(cls => (
                                                <MenuItem key={cls._id} value={cls._id}>{cls.sclassName}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Button 
                                    variant="contained" 
                                    startIcon={<AddIcon />} 
                                    onClick={() => { setEditMode(false); setTabValue(1); }}
                                    sx={{ borderRadius: '12px', px: 3, bgcolor: '#6366f1', fontWeight: 800, height: 40 }}
                                >
                                    New Task
                                </Button>
                            </Box>

                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>
                            ) : filteredHomework.length === 0 ? (
                                <Paper sx={{ p: 10, textAlign: 'center', borderRadius: '24px', border: '2px dashed #e2e8f0', bgcolor: 'transparent' }}>
                                    <TaskIcon sx={{ fontSize: 60, color: '#e2e8f0', mb: 2 }} />
                                    <Typography variant="h5" color="textSecondary" sx={{ fontWeight: 700 }}>No homework found.</Typography>
                                    <Typography color="textSecondary">Assign your first task to see it here.</Typography>
                                    <Button variant="outlined" onClick={() => setTabValue(1)} sx={{ mt: 3, borderRadius: '12px' }}>Create Now</Button>
                                </Paper>
                            ) : (
                                <Grid container spacing={3}>
                                    {filteredHomework.map((hw) => {
                                        const expired = isOverdue(hw.dueDate);
                                        return (
                                            <Grid item xs={12} sm={6} md={4} key={hw._id}>
                                                <HomeworkCard elevation={0}>
                                                    <CardContent sx={{ p: 3 }}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                            <Avatar sx={{ bgcolor: expired ? '#fef2f2' : '#f0fdf4', color: expired ? '#ef4444' : '#10b981' }}>
                                                                {expired ? <OverdueIcon /> : <ActiveIcon />}
                                                            </Avatar>
                                                            <Chip 
                                                                label={expired ? "Expired" : "Active"} 
                                                                color={expired ? "error" : "success"}
                                                                size="small" 
                                                                sx={{ fontWeight: 800 }} 
                                                            />
                                                        </Box>
                                                        
                                                        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, color: '#1e293b', lineClamp: 1, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                            {hw.title}
                                                        </Typography>
                                                        
                                                        <Typography variant="body2" sx={{ color: '#64748b', mb: 2, height: 40, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                                            {hw.description}
                                                        </Typography>
                                                        
                                                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                                            <Chip icon={<ClassIcon sx={{ fontSize: '14px !important' }} />} label={hw.sclass?.sclassName || 'N/A'} size="small" variant="outlined" sx={{ borderRadius: '8px', fontWeight: 600 }} />
                                                            <Chip icon={<SubjectIcon sx={{ fontSize: '14px !important' }} />} label={hw.subject?.subName || 'General'} size="small" variant="outlined" sx={{ borderRadius: '8px', fontWeight: 600 }} />
                                                        </Box>
                                                        
                                                        <Divider sx={{ my: 2, borderStyle: 'dashed' }} />
                                                        
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <TimerIcon sx={{ fontSize: 18, color: expired ? '#ef4444' : '#64748b' }} />
                                                            <Box>
                                                                <Typography variant="caption" color="textSecondary" display="block">DEADLINE</Typography>
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: expired ? '#ef4444' : 'inherit' }}>
                                                                    {formatDate(hw.dueDate)}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </CardContent>
                                                    <CardActions sx={{ p: 2, pt: 0, mt: 'auto', gap: 1 }}>
                                                        <Button 
                                                            variant="contained" 
                                                            startIcon={<EditIcon />} 
                                                            onClick={() => handleEditClick(hw)}
                                                            sx={{ borderRadius: '12px', flexGrow: 1, textTransform: 'none', fontWeight: 700, bgcolor: '#f1f5f9', color: '#1e293b', boxShadow: 'none', '&:hover': { bgcolor: '#e2e8f0', boxShadow: 'none' } }}
                                                        >
                                                            Edit
                                                        </Button>
                                                        <IconButton 
                                                            onClick={() => handleDeleteClick(hw)} 
                                                            sx={{ color: '#ef4444', bgcolor: '#fef2f2', borderRadius: '12px', '&:hover': { bgcolor: '#fee2e2' } }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </CardActions>
                                                </HomeworkCard>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            )}
                        </Box>
                    </Fade>
                ) : (
                    <Fade in timeout={500}>
                        <Box sx={{ mt: 2 }}>
                            <Grid container spacing={4}>
                                <Grid item xs={12} md={7}>
                                    <PremiumCard sx={{ p: 4 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                            <Typography variant="h5" sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 2 }}>
                                                {editMode ? <EditIcon color="primary" /> : <AddIcon color="primary" />} {editMode ? 'Edit Assignment' : 'Assign New Homework'}
                                            </Typography>
                                            {editMode && (
                                                <Button startIcon={<CloseIcon />} onClick={cancelEdit} sx={{ color: '#64748b', fontWeight: 700 }}>Cancel Edit</Button>
                                            )}
                                        </Box>
                                        
                                        <Grid container spacing={3}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Title"
                                                    placeholder="e.g., Mathematics Practice Set 1"
                                                    value={newHomework.title}
                                                    onChange={(e) => setNewHomework({ ...newHomework, title: e.target.value })}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Instructions"
                                                    multiline
                                                    rows={6}
                                                    placeholder="Provide clear instructions for the students..."
                                                    value={newHomework.description}
                                                    onChange={(e) => setNewHomework({ ...newHomework, description: e.target.value })}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth>
                                                    <InputLabel>Class</InputLabel>
                                                    <Select
                                                        value={newHomework.sclassID}
                                                        label="Class"
                                                        onChange={(e) => setNewHomework({ ...newHomework, sclassID: e.target.value, subjectID: '' })}
                                                        sx={{ borderRadius: '12px' }}
                                                    >
                                                        {classes.map((cls) => (
                                                            <MenuItem key={cls._id} value={cls._id}>{cls.sclassName}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth disabled={!newHomework.sclassID}>
                                                    <InputLabel>Subject</InputLabel>
                                                    <Select
                                                        value={newHomework.subjectID}
                                                        label="Subject"
                                                        onChange={(e) => setNewHomework({ ...newHomework, subjectID: e.target.value })}
                                                        sx={{ borderRadius: '12px' }}
                                                    >
                                                        <MenuItem value=""><em>General</em></MenuItem>
                                                        {subjects.map((sub) => (
                                                            <MenuItem key={sub._id} value={sub._id}>{sub.subName}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <CustomNepaliDatePicker
                                                    label="Submission Deadline"
                                                    value={newHomework.dueDate}
                                                    onChange={(date) => setNewHomework({ ...newHomework, dueDate: date })}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Button 
                                                    variant="contained" 
                                                    fullWidth 
                                                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : (editMode ? <SaveIcon /> : <DoneIcon />)}
                                                    onClick={handleCreateHomework}
                                                    disabled={saving}
                                                    sx={{ borderRadius: '12px', py: 2, bgcolor: '#6366f1', fontWeight: 800 }}
                                                >
                                                    {saving ? (editMode ? 'Updating Task...' : 'Assigning Task...') : (editMode ? 'Save Changes' : 'Assign Homework')}
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </PremiumCard>
                                </Grid>
                                
                                <Grid item xs={12} md={5}>
                                    <Box sx={{ position: 'sticky', top: 20 }}>
                                        <PremiumCard sx={{ p: 4, bgcolor: '#eef2ff !important', border: 'none !important' }}>
                                            <Typography variant="h6" sx={{ fontWeight: 900, mb: 2, color: '#1e293b' }}>
                                                Quick Tips 💡
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                <Box sx={{ display: 'flex', gap: 2 }}>
                                                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'white', color: '#6366f1' }}><Typography variant="body2">1</Typography></Avatar>
                                                    <Typography variant="body2" color="textSecondary">
                                                        Be specific with the title to help students find tasks easily.
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 2 }}>
                                                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'white', color: '#6366f1' }}><Typography variant="body2">2</Typography></Avatar>
                                                    <Typography variant="body2" color="textSecondary">
                                                        Mention page numbers or specific exercise names in the description.
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 2 }}>
                                                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'white', color: '#6366f1' }}><Typography variant="body2">3</Typography></Avatar>
                                                    <Typography variant="body2" color="textSecondary">
                                                        Set clear deadlines. Expired tasks will be highlighted in red for both you and the students.
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </PremiumCard>
                                        
                                        <Paper sx={{ p: 4, borderRadius: '24px', textAlign: 'center', bgcolor: '#f8fafc' }}>
                                            <Typography variant="h4" sx={{ fontWeight: 900, color: '#6366f1', mb: 1 }}>{stats.active}</Typography>
                                            <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 700 }}>ACTIVE ASSIGNMENTS</Typography>
                                        </Paper>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </Fade>
                )}
            </ContentContainer>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                PaperProps={{ sx: { borderRadius: '24px', p: 1, maxWidth: '400px' } }}
            >
                <DialogTitle sx={{ fontWeight: 900, fontSize: '1.5rem', pb: 1 }}>Delete Assignment</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#475569', fontWeight: 500 }}>
                        Are you sure you want to delete this homework?
                        <Box component="span" sx={{ color: '#1e293b', fontWeight: 800, display: 'block', mt: 1 }}>
                            "{homeworkToDelete?.title}"
                        </Box>
                        Students will no longer see this task in their portal.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} sx={{ borderRadius: '12px', fontWeight: 700, color: '#64748b', textTransform: 'none' }}>Cancel</Button>
                    <Button 
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                        disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
                        sx={{ borderRadius: '12px', fontWeight: 800, textTransform: 'none', px: 3, bgcolor: '#ef4444' }}
                    >
                        {deleting ? 'Deleting...' : 'Confirm Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageWrapper>
    );
};

const SaveIcon = () => <DoneIcon />; // Alias for consistency

export default Homework;
